import React, { useCallback, useState, useRef, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  ReactFlowProvider,
  ReactFlowInstance,
  Node,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

import ModernSidebar from '../components/ModernSidebar';
import NodeConfigModal from '../components/NodeConfigModal';
import WhatsAppPreview from '../components/WhatsAppPreview';
import FlowValidation from '../components/FlowValidation';
import SmartSuggestions from '../components/SmartSuggestions';
import TemplateGallery from '../components/TemplateGallery';

// Import modern nodes
import ModernTriggerNode from '../components/nodes/ModernTriggerNode';
import ModernActionNode from '../components/nodes/ModernActionNode';
import ModernConditionNode from '../components/nodes/ModernConditionNode';
import ModernFilterNode from '../components/nodes/ModernFilterNode';
import MediaNode from '../components/nodes/MediaNode';
import TemplateNode from '../components/nodes/TemplateNode';
import ChatbotNode from '../components/nodes/ChatbotNode';
import IntegrationNode from '../components/nodes/IntegrationNode';
import AnalyticsNode from '../components/nodes/AnalyticsNode';
import BroadcastNode from '../components/nodes/BroadcastNode';
import DelayNode from '../components/nodes/DelayNode';
import RouterNode from '../components/nodes/RouterNode';

import useFlowStore from '../store/flowStore';
import dbService from '../services/dbService';

const nodeTypes = {
  trigger: ModernTriggerNode,
  action: ModernActionNode,
  condition: ModernConditionNode,
  filter: ModernFilterNode,
  media: MediaNode,
  template: TemplateNode,
  chatbot: ChatbotNode,
  integration: IntegrationNode,
  analytics: AnalyticsNode,
  broadcast: BroadcastNode,
  delay: DelayNode,
  router: RouterNode,
};

let id = 0;
const getId = () => `node_${id++}`;

function Editor() {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [configNode, setConfigNode] = useState<{ id: string; data: any } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedNodeForPreview, setSelectedNodeForPreview] = useState<any>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>(undefined);
  const [showTemplates, setShowTemplates] = useState(false);

  const store = useFlowStore();
  const [nodes, setNodes, onNodesChangeLocal] = useNodesState(store.nodes);
  const [edges, setEdges, onEdgesChangeLocal] = useEdgesState(store.edges);

  // Sync nodes/edges when store changes (e.g., when loading a flow)
  useEffect(() => {
    setNodes(store.nodes);
    setEdges(store.edges);
  }, [store.nodes, store.edges, setNodes, setEdges]);

  // Sync local changes back to store
  const onNodesChange = useCallback((changes: any) => {
    onNodesChangeLocal(changes);
    store.onNodesChange(changes);
  }, [onNodesChangeLocal, store]);

  const onEdgesChange = useCallback((changes: any) => {
    onEdgesChangeLocal(changes);
    store.onEdgesChange(changes);
  }, [onEdgesChangeLocal, store]);

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      const newEdge = {
        ...params,
        animated: true,
        style: { 
          stroke: '#128C7E', 
          strokeWidth: 2,
        },
        type: 'smoothstep',
      };
      store.setEdges(addEdge(newEdge, store.edges));
    },
    [store]
  );

  const onInit = (instance: ReactFlowInstance) => {
    setReactFlowInstance(instance);
  };

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (!reactFlowWrapper.current || !reactFlowInstance) return;

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const data = event.dataTransfer.getData('application/reactflow');
      
      if (!data) return;

      const { nodeType, nodeSubType, label } = JSON.parse(data);

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode: Node = {
        id: getId(),
        type: nodeType,
        position,
        data: {
          label,
          [`${nodeType}Type`]: nodeSubType,
          config: {},
        },
      };

      store.addNode(newNode);
      toast.success(`Nodo "${label}" aggiunto`, {
        icon: '✨',
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
    },
    [reactFlowInstance, store]
  );

  const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    setConfigNode({ id: node.id, data: node.data });
  }, []);

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault();
      
      if (window.confirm(`Vuoi eliminare il nodo "${node.data.label}"?`)) {
        store.deleteNode(node.id);
        toast.success('Nodo eliminato', {
          icon: '🗑️',
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        });
      }
    },
    [store]
  );

  // Test execution visualization with modern effects
  React.useEffect(() => {
    if (store.testMode) {
      const executeFlow = async () => {
        const triggerNodes = store.nodes.filter(n => n.type === 'trigger');
        
        if (triggerNodes.length === 0) {
          toast.error('Nessun trigger trovato nel flow');
          return;
        }

        const startTime = Date.now();
        let hasErrors = false;

        // Log dell'inizio del test
        if (store.currentFlowId) {
          dbService.createLog({
            level: 'info',
            flowId: store.currentFlowId,
            message: 'Test flow avviato',
            details: { nodeCount: store.nodes.length }
          });
        }

        for (const trigger of triggerNodes) {
          toast.success(`Esecuzione trigger: ${trigger.data.label}`, {
            icon: '🚀',
            style: {
              borderRadius: '10px',
              background: '#25D366',
              color: '#fff',
            },
          });
          
          // Highlight node with animation
          const highlightedNode = {
            ...trigger,
            className: 'animate-pulse',
            style: { 
              ...trigger.style, 
              filter: 'drop-shadow(0 0 20px rgba(37, 211, 102, 0.8))',
              transform: 'scale(1.05)',
            }
          };
          
          store.setNodes(
            store.nodes.map(n => n.id === trigger.id ? highlightedNode : n)
          );

          await new Promise(resolve => setTimeout(resolve, 1000));

          // Follow edges and execute connected nodes
          const connectedEdges = store.edges.filter(e => e.source === trigger.id);
          
          for (const edge of connectedEdges) {
            const targetNode = store.nodes.find(n => n.id === edge.target);
            if (targetNode) {
              toast(`Esecuzione: ${targetNode.data.label}`, { 
                icon: '⚡',
                style: {
                  borderRadius: '10px',
                  background: '#34B7F1',
                  color: '#fff',
                },
              });
              
              // Highlight edge with glow
              store.setEdges(
                store.edges.map(e => 
                  e.id === edge.id 
                    ? { 
                        ...e, 
                        animated: true, 
                        style: { 
                          ...e.style, 
                          stroke: '#25D366', 
                          strokeWidth: 3,
                          filter: 'drop-shadow(0 0 10px rgba(37, 211, 102, 0.8))',
                        } 
                      }
                    : e
                )
              );

              await new Promise(resolve => setTimeout(resolve, 800));
            }
          }
        }

        // Reset highlights with smooth transition
        setTimeout(() => {
          store.setNodes(store.nodes.map(n => ({ 
            ...n, 
            className: '',
            style: { ...n.style, filter: '', transform: '' } 
          })));
          store.setEdges(store.edges.map(e => ({ 
            ...e, 
            animated: true,
            style: { 
              stroke: '#128C7E', 
              strokeWidth: 2,
              filter: '',
            } 
          })));
          
          // Registra il completamento del test nel database
          if (store.currentFlowId) {
            const duration = (Date.now() - startTime) / 1000;
            dbService.recordFlowExecution(store.currentFlowId, !hasErrors, duration);
            dbService.createLog({
              level: hasErrors ? 'warning' : 'info',
              flowId: store.currentFlowId,
              message: hasErrors ? 'Test completato con errori' : 'Test completato con successo',
              details: { duration, hasErrors }
            });
          }
        }, 2000);
      };

      executeFlow();
    }
  }, [store.testMode]);

  return (
    <ReactFlowProvider>
      <div className="h-full flex">
        <ModernSidebar />
        
        <div className="flex-1 relative" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={onInit}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeDoubleClick={onNodeDoubleClick}
            onNodeContextMenu={onNodeContextMenu}
            onNodeClick={(event, node) => setSelectedNodeId(node.id)}
            onPaneClick={() => setSelectedNodeId(undefined)}
            nodeTypes={nodeTypes}
            fitView
            attributionPosition="bottom-left"
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: true,
              style: {
                stroke: '#128C7E',
                strokeWidth: 3,
              }
            }}
            className="react-flow-container"
          >
            <Background 
              variant={BackgroundVariant.Dots} 
              gap={20} 
              size={1} 
              color="#e5e7eb"
              className="dark:opacity-10"
            />
            <MiniMap 
              className="!bg-white dark:!bg-dark-surface !border-gray-300 dark:!border-dark-border"
              nodeStrokeColor={(n) => {
                if (n.type === 'trigger') return '#25D366';
                if (n.type === 'action') return '#34B7F1';
                if (n.type === 'condition') return '#FFA726';
                if (n.type === 'filter') return '#AB47BC';
                return '#000';
              }}
              nodeColor={(n) => {
                if (n.type === 'trigger') return '#25D366';
                if (n.type === 'action') return '#34B7F1';
                if (n.type === 'condition') return '#FFA726';
                if (n.type === 'filter') return '#AB47BC';
                return '#fff';
              }}
            />
            <Controls className="!bg-white dark:!bg-dark-surface !border-gray-300 dark:!border-dark-border !shadow-elegant" />
          </ReactFlow>

          {/* Empty State */}
          {nodes.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div className="text-center">
                <div className="text-6xl mb-4">🎯</div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                  Inizia a creare il tuo flow
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md">
                  Trascina un componente dalla sidebar sinistra per iniziare a costruire la tua automazione WhatsApp
                </p>
              </div>
            </motion.div>
          )}
        </div>
        
        {/* Smart Suggestions */}
        <SmartSuggestions selectedNodeId={selectedNodeId} />
        
        {/* Floating Action Buttons */}
        <div className="fixed bottom-8 right-8 flex flex-col gap-3">
          {/* Templates Button */}
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowTemplates(true)}
            className="w-14 h-14 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center"
            title="Template"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 5C4 3.89543 4.89543 3 6 3H18C19.1046 3 20 3.89543 20 5V7C20 8.10457 19.1046 9 18 9H6C4.89543 9 4 8.10457 4 7V5Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M4 13C4 11.8954 4.89543 11 6 11H18C19.1046 11 20 11.8954 20 13V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V13Z" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </motion.button>
          
          {/* Validation Button */}
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowValidation(true)}
            className="w-14 h-14 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-lg flex items-center justify-center"
            title="Valida Flusso"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.button>
          
          {/* Preview Button */}
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
            const actionNodes = store.nodes.filter(n => n.type === 'action');
            if (actionNodes.length > 0) {
              setSelectedNodeForPreview(actionNodes[actionNodes.length - 1]);
              setShowPreview(true);
            } else {
              toast('Aggiungi almeno un nodo azione per vedere l\'anteprima', {
                icon: '💬',
                style: {
                  borderRadius: '10px',
                  background: '#333',
                  color: '#fff',
                },
              });
            }
          }}
            className="w-14 h-14 bg-whatsapp-green hover:bg-whatsapp-dark text-white rounded-full shadow-lg flex items-center justify-center"
            title="Anteprima WhatsApp"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="currentColor"/>
            </svg>
          </motion.button>
        </div>

        {/* Modals */}
        {configNode && (
          <NodeConfigModal
            nodeId={configNode.id}
            nodeData={configNode.data}
            onClose={() => setConfigNode(null)}
          />
        )}
        
        <WhatsAppPreview
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          selectedNode={selectedNodeForPreview}
          flow={store.nodes}
        />
        
        <FlowValidation
          isOpen={showValidation}
          onClose={() => setShowValidation(false)}
        />
        
        <TemplateGallery
          isOpen={showTemplates}
          onClose={() => setShowTemplates(false)}
        />
        
      </div>
    </ReactFlowProvider>
  );
}

export default Editor;
